import React, { useState } from "react";
import { Table, Button, Card, Modal, Form, Badge } from "react-bootstrap";
import {
  useGetJobCategoriesQuery,
  useCreateJobCategoryMutation,
  useUpdateJobCategoryMutation,
  useDeleteJobCategoryMutation,
  useToggleJobCategoryStatusMutation,
} from "../../../redux/api/CareersApi/careersApi";
import LoaderSpinner from "../../../Components/LoaderSpinner";
import { FaPlus, FaEdit, FaTrash, FaTags } from "react-icons/fa";
import Swal from "sweetalert2";
import { message } from "antd";

const JobCategories = () => {
  const { data: categories, isLoading } = useGetJobCategoriesQuery();
  const [createCategory] = useCreateJobCategoryMutation();
  const [updateCategory] = useUpdateJobCategoryMutation();
  const [deleteCategory] = useDeleteJobCategoryMutation();
  const [toggleStatus] = useToggleJobCategoryStatusMutation();

  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({ name: "", display_order: 0 });

  const handleOpenModal = (cat = null) => {
    if (cat) {
      setEditingCategory(cat);
      setFormData({ name: cat.name, display_order: cat.display_order });
    } else {
      setEditingCategory(null);
      setFormData({ name: "", display_order: 0 });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const slug = formData.name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
    try {
      if (editingCategory) {
        await updateCategory({
          id: editingCategory.id,
          ...formData,
          slug,
        }).unwrap();
        message.success("Category updated");
      } else {
        await createCategory({ ...formData, slug }).unwrap();
        message.success("Category created");
      }
      setShowModal(false);
    } catch (error) {
      message.error(error.data?.error || "Operation failed");
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This may affect jobs linked to this category.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await deleteCategory(id).unwrap();
        Swal.fire("Deleted!", "Category has been removed.", "success");
      } catch (error) {
        Swal.fire("Error", "Failed to delete category", "error");
      }
    }
  };

  const handleToggle = async (id) => {
    try {
      await toggleStatus(id).unwrap();
    } catch (error) {
      message.error("Failed to update status");
    }
  };

  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "60vh" }}
      >
        <LoaderSpinner />
      </div>
    );
  }

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold text-dark mb-1">
            <FaTags className="text-primary me-2" />
            Job Categories
          </h2>
          <p className="text-muted mb-0">
            Manage job categories for careers page
          </p>
        </div>
        <Button
          variant="primary"
          onClick={() => handleOpenModal()}
          className="rounded-pill px-4"
        >
          <FaPlus /> Add Category
        </Button>
      </div>

      <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
        <Table hover className="align-middle mb-0">
          <thead className="bg-light text-muted small text-uppercase">
            <tr>
              <th className="ps-4">Category Name</th>
              <th>Slug</th>
              <th className="text-center">Order</th>
              <th className="text-center">Status</th>
              <th className="text-end pe-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {categories?.map((cat) => (
              <tr key={cat.id}>
                <td className="ps-4 py-3 fw-bold">{cat.name}</td>
                <td>
                  <code>{cat.slug}</code>
                </td>
                <td className="text-center">{cat.display_order}</td>
                <td className="text-center">
                  <Badge
                    bg={cat.status ? "success" : "danger"}
                    className="cursor-pointer"
                    onClick={() => handleToggle(cat.id)}
                  >
                    {cat.status ? "Active" : "Disabled"}
                  </Badge>
                </td>
                <td className="text-end pe-4">
                  <div className="d-flex justify-content-end gap-2">
                    <Button
                      variant="light"
                      size="sm"
                      onClick={() => handleOpenModal(cat)}
                    >
                      <FaEdit className="text-primary" />
                    </Button>
                    <Button
                      variant="light"
                      size="sm"
                      onClick={() => handleDelete(cat.id)}
                    >
                      <FaTrash className="text-danger" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="fw-bold">
            {editingCategory ? "Update Category" : "Add New Category"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">Category Name</Form.Label>
              <Form.Control
                type="text"
                required
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="e.g. Design"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">Display Order</Form.Label>
              <Form.Control
                type="number"
                value={formData.display_order}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    display_order: parseInt(e.target.value),
                  })
                }
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="border-0">
            <Button variant="light" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {editingCategory ? "Update" : "Create"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default JobCategories;
